import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IContentStateChangeResponseDto } from 'src/app/core/dtos/interfaces/i-content-state-change-response-dto';
import { IGetDigitalPublicationDto } from 'src/app/core/dtos/interfaces/i-get-digital-publication-dto';
import { ISaveDigitalPublicationDto } from 'src/app/core/dtos/interfaces/i-save-digital-publication-dto';
import { INewContentVersionResponseDto } from 'src/app/core/dtos/interfaces/i-new-content-version-response-dto';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { ISpikeContentResponseDto } from 'src/app/core/dtos/interfaces/i-spike-content-response-dto';
import { RestService } from 'src/app/shared/services/rest-service';
import { IDigitalPublicationContentDto } from 'src/app/core/dtos/interfaces/i-digital-publication-content-dto';
import { map } from 'rxjs/operators';
import { DigitalPublicationContentDto } from 'src/app/core/dtos/digital-publication-content-dto';

@Injectable({
  providedIn: 'root',
})
export class DigitalPublicationContentTypeService {
  static readonly ENTITY_IDENTIFIER: string = 'digitalPublications';
  private readonly BASE_CONTENT_ROUTE = `api/${DigitalPublicationContentTypeService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_CONTENT_ROUTE, ...args);

  constructor(private restService: RestService) {}
  create$(
    request: IDigitalPublicationContentDto
  ): Observable<ISaveContentResponseDto> {
    return this.restService.post$<ISaveContentResponseDto>(
      this.BUILD_HTTP_ROUTE(),
      request
    );
  }

  createVersion$(
    contentId: string,
    versionId: string
  ): Observable<INewContentVersionResponseDto> {
    return this.restService.post$<INewContentVersionResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}/versions/${versionId}`),
      null
    );
  }

  deleteVersion$(contentId: string, versionId: string): Observable<Object> {
    const params = {};
    return this.restService.remove$(
      this.BUILD_HTTP_ROUTE(`${contentId}/versions/${versionId}`),
      params
    );
  }

  getById$(
    contentId: string,
    versionId?: string,
    returnEvenDeleted: boolean = false
  ): Observable<IDigitalPublicationContentDto> {
    const params = this.restService.buildHttpParamsObject({
      versionId: versionId,
      returnEvenDeleted: returnEvenDeleted,
    });
    return this.restService
      .get$<IDigitalPublicationContentDto>(
        this.BUILD_HTTP_ROUTE(`${contentId}`),
        { params }
      )
      .pipe(map((x) => new DigitalPublicationContentDto(x)));
  }

  getByVersionId$(
    versionId?: string,
    returnEvenDeleted: boolean = false
  ): Observable<IDigitalPublicationContentDto> {
    const params = this.restService.buildHttpParamsObject({
      versionId: versionId,
      returnEvenDeleted: returnEvenDeleted,
    });
    return this.restService
      .get$<IDigitalPublicationContentDto>(this.BUILD_HTTP_ROUTE(`-`), {
        params,
      })
      .pipe(map((x) => new DigitalPublicationContentDto(x)));
  }

  update$(
    contentId: string,
    contentVersion: IDigitalPublicationContentDto
  ): Observable<ISaveContentResponseDto> {
    return this.restService.put$<ISaveContentResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}`),
      contentVersion
    );
  }

  delete$(
    contentId: string,
    overrideDeleteDate: Date = null
  ): Observable<Object> {
    const params = this.restService.buildHttpParamsObject({
      overrideDeleteDate: overrideDeleteDate,
    });
    return this.restService.remove$(this.BUILD_HTTP_ROUTE(`${contentId}`), {
      params,
    });
  }

  spike$(
    contentId: string,
    reinstate: boolean = false
  ): Observable<ISpikeContentResponseDto> {
    const params = this.restService.buildHttpParamsObject({
      reinstate: reinstate,
    });
    return this.restService.put$<ISpikeContentResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}/spike`),
      { params }
    );
  }

  updateState$(
    versionId: string,
    status: string
  ): Observable<IContentStateChangeResponseDto> {
    return this.restService.put$<IContentStateChangeResponseDto>(
      this.BUILD_HTTP_ROUTE(`versions/${versionId}/state/${status}`),
      null
    );
  }
}
